"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScreenshotUploadUrlResponseSchema = exports.ScreenshotUploadUrlRequestSchema = exports.RejectFixRequestSchema = exports.ConfirmFixRequestSchema = exports.AddIssueCommentRequestSchema = exports.UpdateAppIssueAdminRequestSchema = exports.CreateAppIssueRequestSchema = exports.AppIssueCommentSchema = exports.AppIssueSchema = exports.AppIssueAffectedDeviceSchema = exports.AppIssueContextSchema = exports.AppIssueNetworkEntrySchema = exports.AppIssueConsoleEntrySchema = exports.IssueCommentKindSchema = exports.AppIssueSourceSchema = exports.TicketDeviceRefSchema = exports.TicketContactSchema = exports.TicketKindSchema = exports.DEVICE_AUTO_CATEGORY_LABEL = exports.DEVICE_AUTO_CATEGORY = exports.AppIssueCategorySchema = exports.AppIssuePrioritySchema = exports.AppIssueStatusSchema = void 0;
const zod_1 = require("zod");
// ---------------------------------------------------------------------------
// AppIssue — in-app issue reports submitted from platform-app
//
// Distinct from the public "support tickets" (contact-form inquiries handled
// by leads-service). An AppIssue is tied to a logged-in user and carries
// auto-captured page context (route, screenshot, console errors, page state)
// so an AI agent or super admin can reproduce and fix it.
// ---------------------------------------------------------------------------
exports.AppIssueStatusSchema = zod_1.z.enum([
    'new',
    'in_progress',
    'deploying',
    'awaiting_retest',
    'resolved',
    'rejected',
    'awaiting_owner_approval',
]);
exports.AppIssuePrioritySchema = zod_1.z.enum([
    'minor',
    'annoying',
    'blocking',
    'critical',
]);
exports.AppIssueCategorySchema = zod_1.z.enum([
    'ui_bug',
    'broken_feature',
    'performance',
    'data_issue',
    'feature_request',
    'confusing',
    'crash',
    'calculation',
    'other',
    // Device health auto-tickets (spec 2026-10-04). Written only by
    // device-health-service; one ticket per problem per store.
    'device_auto',
]);
/** The auto-ticket category and the words the Issue Console shows for it. */
exports.DEVICE_AUTO_CATEGORY = 'device_auto';
exports.DEVICE_AUTO_CATEGORY_LABEL = 'Android devices · POS auto';
// ---------------------------------------------------------------------------
// Unified support center (2026-08-26) — lets a single AppIssue represent app
// bugs, device problems, billing questions, and reporterless contact-form
// inquiries, and lets a comment record a phone call or a status change.
// ---------------------------------------------------------------------------
exports.TicketKindSchema = zod_1.z.enum([
    'issue',
    'device',
    'billing',
    'question',
    'contact',
]);
exports.TicketContactSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(120),
    email: zod_1.z.string().email().max(254),
    phone: zod_1.z.string().max(32).optional(),
    preferredMethod: zod_1.z.enum(['email', 'phone']).optional(),
});
exports.TicketDeviceRefSchema = zod_1.z.object({
    unitId: zod_1.z.string().optional(),
    serialNumber: zod_1.z.string().optional(),
    modelId: zod_1.z.string().optional(),
    modelName: zod_1.z.string().optional(),
    manufacturer: zod_1.z.string().optional(),
    terminalId: zod_1.z.string().optional(),
    registerName: zod_1.z.string().optional(),
    locationId: zod_1.z.string().optional(),
    locationName: zod_1.z.string().optional(),
    terminalType: zod_1.z.string().optional(),
});
exports.AppIssueSourceSchema = zod_1.z.enum([
    'web',
    'pos-terminal',
    'public',
    // Filed by device-health-service from silent till telemetry — no human reporter.
    'pos-auto',
]);
exports.IssueCommentKindSchema = zod_1.z.enum([
    'comment',
    'status_change',
    'phone_call',
]);
exports.AppIssueConsoleEntrySchema = zod_1.z.object({
    level: zod_1.z.enum(['log', 'info', 'warn', 'error']),
    message: zod_1.z.string(),
    stack: zod_1.z.string().optional(),
    timestamp: zod_1.z.number().int(),
});
exports.AppIssueNetworkEntrySchema = zod_1.z.object({
    method: zod_1.z.string(),
    url: zod_1.z.string(),
    status: zod_1.z.number().int(),
    durationMs: zod_1.z.number().int().optional(),
    responseSnippet: zod_1.z.string().max(500).optional(),
    timestamp: zod_1.z.number().int(),
});
exports.AppIssueContextSchema = zod_1.z.object({
    route: zod_1.z.string(), // e.g. "/pos/transactions"
    pageTitle: zod_1.z.string().optional(), // e.g. "POS > Transactions"
    userId: zod_1.z.string(),
    userEmail: zod_1.z.string().email(),
    userRole: zod_1.z.string(), // 'admin' | 'manager' | 'employee' | ...
    tenantId: zod_1.z.string(),
    tenantName: zod_1.z.string().optional(),
    productId: zod_1.z.string(), // 'cafemaster' | 'agenticpos' | ...
    browser: zod_1.z.string().optional(), // 'Safari 17'
    os: zod_1.z.string().optional(), // 'iPadOS 17.4'
    userAgent: zod_1.z.string().optional(),
    viewport: zod_1.z.object({
        width: zod_1.z.number().int(),
        height: zod_1.z.number().int(),
    }).optional(),
    appVersion: zod_1.z.string().optional(),
    bundleVersion: zod_1.z.string().optional(),
});
/** One till inside a device auto-ticket. */
exports.AppIssueAffectedDeviceSchema = zod_1.z.object({
    deviceId: zod_1.z.string(),
    registerName: zod_1.z.string().optional(),
    count: zod_1.z.number().int().nonnegative(),
    lastSeen: zod_1.z.number().int(),
});
exports.AppIssueSchema = zod_1.z.object({
    id: zod_1.z.string(), // 'iss_<ulid>'
    tenantId: zod_1.z.string(),
    reporterId: zod_1.z.string(), // userId of submitter
    reporterEmail: zod_1.z.string().email(),
    reporterName: zod_1.z.string().optional(),
    title: zod_1.z.string().min(3).max(160),
    description: zod_1.z.string().max(4000),
    category: exports.AppIssueCategorySchema,
    priority: exports.AppIssuePrioritySchema,
    status: exports.AppIssueStatusSchema,
    // Unified support center — what kind of ticket this is, where it was filed
    // from, and (for device/contact tickets) who/what it's about.
    kind: exports.TicketKindSchema.optional(), // defaults to 'issue' when absent
    source: exports.AppIssueSourceSchema.optional(),
    contact: exports.TicketContactSchema.optional(),
    device: exports.TicketDeviceRefSchema.optional(),
    context: exports.AppIssueContextSchema,
    // Attachments / diagnostics (all optional — capture failures shouldn't block)
    screenshotKey: zod_1.z.string().optional(), // S3 key
    consoleErrors: zod_1.z.array(exports.AppIssueConsoleEntrySchema).max(50).optional(),
    networkFailures: zod_1.z.array(exports.AppIssueNetworkEntrySchema).max(20).optional(),
    pageState: zod_1.z.string().max(10000).optional(), // JSON-stringified snapshot
    // Workflow
    assigneeId: zod_1.z.string().optional(), // super admin handling it
    /**
     * The assignee's email, recorded ALONGSIDE `assigneeId` so a tester can be
     * matched across stages.
     *
     * Dev and prod have separate admin Cognito pools, so the same human has a
     * different `sub` in each — `assigneeId` is therefore meaningless outside
     * the stage that wrote it, and the console's cross-stage queue cannot use
     * it. Email means the same thing in both pools.
     *
     * Nullable because `null` — never `undefined` — is how a field is cleared:
     * JSON drops an undefined key and every merge keeps the stale value. That
     * matters here more than usual: a stale assignee email after a reassignment
     * would show one tester another tester's issues.
     */
    assigneeEmail: zod_1.z.string().nullable().optional(),
    notifyByEmail: zod_1.z.boolean().default(true),
    resolvedAt: zod_1.z.number().int().optional(),
    resolvedBy: zod_1.z.string().optional(),
    resolutionNote: zod_1.z.string().max(2000).optional(),
    confirmedAt: zod_1.z.number().int().optional(), // reporter confirmed fix
    rejectedAt: zod_1.z.number().int().optional(), // reporter said still broken
    rejectionNote: zod_1.z.string().max(2000).optional(),
    // Issue Fix Platform (2026-09-26) — joins an issue to the isolated branch
    // Claude worked on, its dev-only preview, and the rolled-up cost across
    // every attempt. All optional: every row written before today lacks them.
    /** The isolated branch Claude is working on, e.g. issue/423-recall-hold-tax. */
    branch: zod_1.z.string().optional(),
    /** Dev-only preview URL the tester verifies against. Never a prod host. */
    previewUrl: zod_1.z.string().url().optional(),
    /** 1-based attempt counter; incremented each time a tester sends it back. */
    attempt: zod_1.z.number().int().positive().optional(),
    /** Rolled-up Claude spend across every attempt, USD. */
    totalCostUsd: zod_1.z.number().nonnegative().optional(),
    /**
     * The owner's single-use deploy token for this issue, minted by
     * POST /admin/issues/{id}/deploy-token and spent by .../deploy.
     *
     * It lives on the issue row rather than in a Lambda's memory because mint
     * and redeem are two different invocations in two different containers;
     * single use is enforced by a conditional update on `used = false`. It is
     * declared here so a `AppIssueSchema.parse()` round-trip cannot silently
     * strip it — a vanishing deploy token on the gate in front of production
     * would fail open-ish and be invisible. Carries no secret: no code and no
     * TOTP material, only what the checks need.
     */
    deployToken: zod_1.z.object({
        tokenId: zod_1.z.string(),
        userId: zod_1.z.string(),
        issuedAt: zod_1.z.number(),
        used: zod_1.z.boolean(),
        failures: zod_1.z.number().int().nonnegative(),
        // The rest of what the service actually persists inside this object. A
        // nested z.object strips unknown keys too, so omitting these would strip
        // exactly the fields that decide WHAT gets deployed — leaving a token
        // that passes every check and then dispatches nothing. Optional, because
        // a token minted before this field existed carries none of them.
        /** The issue this token was minted for — it is never valid for another. */
        issueId: zod_1.z.string().optional(),
        /** Repos bound at mint time, in dispatch order: backend first, then the app. */
        repos: zod_1.z.array(zod_1.z.string()).optional(),
        /** The issue's own fix branch, bound at mint time. */
        ref: zod_1.z.string().optional(),
    }).optional(),
    /**
     * The changed-file list for this issue's fix branch, stamped by the fixer's
     * CI callback (`POST /internal/fix-runs`) and the ONLY input the console's
     * change-impact panel classifies. Without it `classifyChange` has nothing to
     * bucket, so the hold/review/safe verdict reads "unknown" forever — which is
     * why it is declared here rather than left as an undeclared row attribute:
     * Zod strips unknown keys, so an undeclared field is a field that silently
     * disappears on the first `AppIssueSchema.parse()` round-trip.
     *
     * `added`/`removed` are presentational line counts; the verdict never
     * depends on them. Absent on every row written before 2026-09-26.
     */
    changedFiles: zod_1.z.array(zod_1.z.object({
        // `.min(1)` so the schema is not LAXER than `assertChangedFiles` in
        // support-service, which already refuses an empty path. A schema that
        // accepts what the validator rejects is a gap someone will fall into.
        path: zod_1.z.string().min(1),
        status: zod_1.z.enum(['A', 'M', 'D']),
        added: zod_1.z.number().int().nonnegative(),
        removed: zod_1.z.number().int().nonnegative(),
    })).optional(),
    /**
     * Who passed the retest. Both fields have been WRITTEN by `confirmFix`
     * since the tester console shipped but were never declared here, so a parse
     * stripped them and the approval queue's "tested by" column went blank.
     * Nullable because `null` — never `undefined` — is how a field is cleared:
     * JSON drops an undefined key and every merge keeps the stale value.
     */
    confirmedBy: zod_1.z.string().nullable().optional(),
    confirmedByEmail: zod_1.z.string().nullable().optional(),
    // -------------------------------------------------------------------------
    // The ship / rollback record (I5).
    //
    // Every field below has been WRITTEN by the deploy gate, the rollback path
    // or a CI callback and READ BACK by `rollback.rollbackHandler` since the
    // platform shipped, while being declared nowhere. Zod strips unknown keys,
    // so one `AppIssueSchema.parse()` round-trip anywhere in the platform drops
    // them — and the first five are precisely what decides whether a rollback is
    // possible and which commit it returns production to. A vanishing
    // `lastGoodSha` does not fail loudly; it makes `planRollback` say "nothing
    // to roll back to" about a fix that is live on real tills.
    //
    // All optional: every row written before 2026-09-26 carries none of them.
    // -------------------------------------------------------------------------
    /** The commit production was running before this fix shipped (first repo). */
    lastGoodSha: zod_1.z.string().optional(),
    /**
     * Per-repo last-good commits. Repos do not share a history, so one sha
     * cannot be right for all of them; `lastGoodSha` is the fallback for rows
     * written before this map existed.
     */
    lastGoodShas: zod_1.z.record(zod_1.z.string(), zod_1.z.string()).optional(),
    /** The commit that shipped — what a rollback reverts and restores the branch at. */
    shippedSha: zod_1.z.string().optional(),
    /** When it shipped. The 24-hour rollback window is measured from this. */
    shippedAt: zod_1.z.number().int().optional(),
    /** The repos that were dispatched, in forward order: backend first, then the app. */
    shippedRepos: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * The fix run that was DISPATCHED for this issue — the loop's front door.
     *
     * Written by POST /admin/issues/{id}/console/fix (and by reject-fix, because a
     * rejection is the next brief) after GitHub accepts the workflow_dispatch.
     * `note` is the human's own sentence, which on a rejection is the most useful
     * input the agent gets.
     *
     * Read by the in-flight guard that stops two agents landing on one branch, so
     * leaving it undeclared would make a parse strip it and the guard silently
     * never fire. `by` is nullable rather than optional: `null` says the
     * dispatcher is not recorded, where `''` would read as someone with a blank id.
     */
    fixRun: zod_1.z.object({
        attempt: zod_1.z.number().int().positive(),
        dispatchedAt: zod_1.z.number().int(),
        by: zod_1.z.string().nullable().optional(),
        repo: zod_1.z.string().optional(),
        note: zod_1.z.string().optional(),
    }).optional(),
    /**
     * When a fix run last REPORTED BACK, stamped by POST /internal/fix-runs.
     *
     * This is the completion marker the in-flight guard compares against
     * `fixRun.dispatchedAt`: a dispatch newer than the last report means a run is
     * still out there. A timestamp rather than a nested `fixRun.completedAt`
     * because a DynamoDB `SET fixRun.completedAt` fails outright when `fixRun` is
     * absent — which it is for every run not started from the console — and a
     * callback must never fail over bookkeeping.
     */
    lastFixRunAt: zod_1.z.number().int().optional(),
    /**
     * WHO HAS THIS ISSUE CHECKED OUT in the tester console -- a LEASE, not a
     * permanent lock. Taken by "Check out" or the holder's first action, renewed
     * by the open console tab, and void once `expiresAt` passes, so a crashed tab
     * can never hold an issue forever. While it is live, the verdicts, start-fix
     * and status writes refuse anyone else (409 naming the holder).
     *
     * `takenOverFrom` records a forced take-over, so the previous holder's
     * console can say who took it. Optional and additive; `null` = released.
     */
    lock: zod_1.z.object({
        userId: zod_1.z.string(),
        name: zod_1.z.string().nullable().optional(),
        email: zod_1.z.string().nullable().optional(),
        acquiredAt: zod_1.z.number().int(),
        expiresAt: zod_1.z.number().int(),
        takenOverFrom: zod_1.z.string().nullable().optional(),
    }).nullable().optional(),
    /**
     * The dispatched deploy, as far as the service can honestly observe it.
     *
     * `runId` is `${issueId}:${dispatchedAt}` and is NOT a GitHub run id:
     * `workflow_dispatch` answers 204 with an empty body, so no run id exists to
     * record. Nothing here should ever be read as workflow progress.
     */
    deployRun: zod_1.z.object({
        runId: zod_1.z.string(),
        repos: zod_1.z.array(zod_1.z.string()),
        ref: zod_1.z.string(),
        dispatchedAt: zod_1.z.number().int(),
        environment: zod_1.z.enum(['dev', 'prod']).optional(),
    }).optional(),
    /**
     * What the deploy workflow reported back through POST /internal/deploy-result.
     * `ok: false` leaves the status at `deploying` on purpose — a partly-failed
     * deploy is exactly when rollback must stay reachable.
     */
    deployResult: zod_1.z.object({
        ok: zod_1.z.boolean(),
        at: zod_1.z.number().int(),
        runId: zod_1.z.string().optional(),
        repos: zod_1.z.array(zod_1.z.string()).optional(),
        /** Verbatim workflow output. A summary of a tsc error is not a tsc error. */
        failureLog: zod_1.z.array(zod_1.z.string()).optional(),
        elapsedSeconds: zod_1.z.number().nonnegative().optional(),
        workflow: zod_1.z.string().optional(),
    }).optional(),
    /**
     * The executed rollback. `plan` is `z.unknown()` rather than a nested object
     * on purpose: a nested `z.object` strips ITS unknown keys too, and this is an
     * audit record of what was actually done — a partially stripped plan is a
     * misleading one.
     */
    rollback: zod_1.z.object({
        at: zod_1.z.number().int(),
        by: zod_1.z.string().nullable().optional(),
        tag: zod_1.z.string().optional(),
        plan: zod_1.z.unknown().optional(),
    }).optional(),
    /**
     * The stored output of `classifyChange` for this fix. `rollback.ts` reads it
     * to decide whether an automatic rollback is safe at all, so stripping it
     * silently downgrades a `hold` change to "unclassified", which is allowed
     * through.
     */
    changeImpact: zod_1.z.object({
        additiveOnly: zod_1.z.boolean().optional(),
        modifiesExisting: zod_1.z.array(zod_1.z.string()).optional(),
        infrastructure: zod_1.z.array(zod_1.z.string()).optional(),
        stepFunctions: zod_1.z.array(zod_1.z.string()).optional(),
        queues: zod_1.z.array(zod_1.z.string()).optional(),
        schema: zod_1.z.array(zod_1.z.string()).optional(),
        moneyPath: zod_1.z.array(zod_1.z.string()).optional(),
        migrationRequired: zod_1.z.boolean().optional(),
        verdict: zod_1.z.enum(['safe', 'review', 'hold']).optional(),
    }).optional(),
    /**
     * The repro seed script on the fix branch, written only when the bug was
     * reported on prod and cannot be reproduced by clicking around dev.
     */
    seedScript: zod_1.z.string().optional(),
    // CI verification facts the owner's review panel renders. Each is ABSENT
    // when its producer has not run — never 0 or false, which would claim a
    // suite ran and nothing passed.
    testsPassed: zod_1.z.number().int().nonnegative().optional(),
    testsFailed: zod_1.z.number().int().nonnegative().optional(),
    typesClean: zod_1.z.boolean().optional(),
    protectedPathsOk: zod_1.z.boolean().optional(),
    devOnlyBundleVerified: zod_1.z.boolean().optional(),
    elapsedSeconds: zod_1.z.number().nonnegative().optional(),
    /**
     * Which stage's table this row came from — `dev` or `prod`. Stamped by the
     * console's cross-stage merge, because ids are NOT unique across stages and
     * every consumer keys on `${sourceStage}:${id}`.
     */
    sourceStage: zod_1.z.string().optional(),
    // -------------------------------------------------------------------------
    // Device health auto-tickets (spec 2026-10-04). Additive and optional: every
    // human-filed ticket lacks them. Declared because Zod strips undeclared keys
    // and the worker validates with AppIssueSchema.parse() before writing.
    // -------------------------------------------------------------------------
    /** Total occurrences of this problem at this store. */
    occurrenceCount: zod_1.z.number().int().nonnegative().optional(),
    /** Last occurrence, epoch ms. */
    lastSeenAt: zod_1.z.number().int().optional(),
    /** Tills that hit it, most occurrences first (max 20). */
    affectedDevices: zod_1.z.array(exports.AppIssueAffectedDeviceSchema).max(20).optional(),
    /** sha1 problem fingerprint — joins the ticket to device-health's problem row. */
    fingerprint: zod_1.z.string().optional(),
    createdAt: zod_1.z.number().int(),
    updatedAt: zod_1.z.number().int(),
});
// ---------------------------------------------------------------------------
// AppIssueComment — threaded comments on an issue (reporter ↔ super admin)
// ---------------------------------------------------------------------------
exports.AppIssueCommentSchema = zod_1.z.object({
    id: zod_1.z.string(), // 'cmt_<ulid>'
    issueId: zod_1.z.string(),
    authorId: zod_1.z.string(),
    authorEmail: zod_1.z.string().email(),
    authorName: zod_1.z.string().optional(),
    authorRole: zod_1.z.enum(['reporter', 'super_admin', 'system']),
    body: zod_1.z.string().min(1).max(2000),
    internal: zod_1.z.boolean().default(false), // hidden from reporter when true
    kind: exports.IssueCommentKindSchema.default('comment'),
    createdAt: zod_1.z.number().int(),
});
// ---------------------------------------------------------------------------
// API DTOs
// ---------------------------------------------------------------------------
exports.CreateAppIssueRequestSchema = zod_1.z.object({
    title: zod_1.z.string().min(3).max(160),
    description: zod_1.z.string().max(4000),
    category: exports.AppIssueCategorySchema,
    priority: exports.AppIssuePrioritySchema,
    kind: exports.TicketKindSchema.optional(),
    contact: exports.TicketContactSchema.optional(),
    device: exports.TicketDeviceRefSchema.optional(),
    context: exports.AppIssueContextSchema,
    consoleErrors: zod_1.z.array(exports.AppIssueConsoleEntrySchema).max(50).optional(),
    networkFailures: zod_1.z.array(exports.AppIssueNetworkEntrySchema).max(20).optional(),
    pageState: zod_1.z.string().max(10000).optional(),
    screenshotKey: zod_1.z.string().optional(), // S3 key uploaded via presigned URL
    notifyByEmail: zod_1.z.boolean().default(true),
});
exports.UpdateAppIssueAdminRequestSchema = zod_1.z.object({
    status: exports.AppIssueStatusSchema.optional(),
    priority: exports.AppIssuePrioritySchema.optional(),
    assigneeId: zod_1.z.string().optional(),
    resolutionNote: zod_1.z.string().max(2000).optional(),
});
exports.AddIssueCommentRequestSchema = zod_1.z.object({
    body: zod_1.z.string().min(1).max(2000),
    internal: zod_1.z.boolean().default(false),
});
exports.ConfirmFixRequestSchema = zod_1.z.object({
    note: zod_1.z.string().max(2000).optional(),
});
exports.RejectFixRequestSchema = zod_1.z.object({
    note: zod_1.z.string().min(1).max(2000),
});
exports.ScreenshotUploadUrlRequestSchema = zod_1.z.object({
    contentType: zod_1.z.string().default('image/png'),
    contentLengthBytes: zod_1.z.number().int().positive().max(5 * 1024 * 1024), // 5MB cap
});
exports.ScreenshotUploadUrlResponseSchema = zod_1.z.object({
    uploadUrl: zod_1.z.string().url(),
    key: zod_1.z.string(),
    expiresInSeconds: zod_1.z.number().int(),
});
//# sourceMappingURL=app-issue.schema.js.map